import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { SupabaseProvider } from "./SupabaseContext";
import SoluterApp from "./components/SoluterApp";
import AdminPage from "./components/AdminRegistrationForm";
import AdminDashboard from "./components/AdminDashboard";
import LoginPage from "./components/LoginPage";
import SituationsPage from "./components/SituationsPage";
import SituationDetail from "./components/SituationDetail";
import EvaluateSolutions from "./components/EvaluateSolutions";
import CompanyDashboard from "./components/CompanyDashboard";
import FacilityManagement from "./components/FacilityManagement";
import ManagerManagement from "./components/ManagerManagement";
import CustomCategoriesManagement from "./components/CustomCategoriesManagement";
import ResponsiveNavbar from "./components/ResponsiveNavbar";
import { Toaster } from "./components/ui/toaster";
import ResetPasswordPage from "./components/ResetPasswordPage";

const App = () => {
  return (
    <SupabaseProvider>
      <Router>
        <div className="flex flex-col min-h-screen bg-gray-50">
          {/* Replace old navbar with the new responsive one */}
          <ResponsiveNavbar />

          <main className="flex flex-grow justify-center items-center px-0 py-0 sm:px-6 lg:px-8 sm:py-6">
            <Routes>
              <Route path="/" element={<Navigate to="/login" />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/situations"
                element={
                  <ProtectedRoute>
                    <SituationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/situation/:id"
                element={
                  <ProtectedRoute>
                    <SituationDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/evaluate-solutions/:id"
                element={
                  <ProtectedRoute>
                    <EvaluateSolutions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <ProtectedRoute requiresAdmin>
                    <CustomCategoriesManagement />
                  </ProtectedRoute>
                }
              />
              {/* Admin-only routes */}
              <Route
                path="/companies"
                element={
                  <ProtectedRoute adminOnly>
                    <CompanyDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:companyId/facilities"
                element={
                  <ProtectedRoute adminOnly>
                    <FacilityManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:companyId/managers"
                element={
                  <ProtectedRoute adminOnly>
                    <ManagerManagement />
                  </ProtectedRoute>
                }
              />
              {/* Routes for both admin and manager */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiresAdmin>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Routes>
          </main>

          <footer className="bg-white border-t border-gray-200">
            <div className="flex flex-col justify-between items-center px-4 py-4 mx-auto space-y-2 max-w-7xl sm:px-6 lg:px-8 sm:flex-row sm:space-y-0">
              <p className="text-sm text-gray-500">Soluteria v1.0.0</p>
              <p className="text-sm text-gray-500">
                {new Date().getFullYear()} © Všetky práva vyhradené
              </p>
            </div>
          </footer>
          <Toaster />
        </div>
      </Router>
    </SupabaseProvider>
  );
};

const ProtectedRoute = ({ children, requiresAdmin, adminOnly }) => {
  const user = JSON.parse(localStorage.getItem("user"));

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Only allow admin users to access company routes
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/situations" />;
  }

  // Less strict check for routes that allow both admins and managers
  if (requiresAdmin && !user.is_admin && !user.is_manager) {
    return <Navigate to="/situations" />;
  }

  return children;
};

export default App;
